import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { map, tap, catchError, shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface Division {
  id: number;
  divisionCode: string;
  divisionName: string;
}

export interface Rule {
  id: number;
  ruleName: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  applicableDivisions?: string;
  divisions: Division[];
}

export interface RuleScope {
  ruleName: string;
  applyToAllWarehouses: boolean;
  warehouses: string[];
}

const OUT_OF_STOCK_RULE = 'OUT_OF_STOCK_QTY';

@Injectable({
  providedIn: 'root'
})
export class RulesService {
  private readonly scopeCache = new Map<string, RuleScope[]>();
  private readonly scopeLoaders = new Map<string, Observable<RuleScope[]>>();

  private ruleActiveForDivisionSubject = new BehaviorSubject<boolean>(false);
  ruleActiveForDivision$ = this.ruleActiveForDivisionSubject.asObservable();

  constructor(private http: HttpClient) {}

  /**
   * Load rule scopes for a division (cached per session).
   */
  loadRuleScopes(divisionCode: string): Observable<RuleScope[]> {
    const div = String(divisionCode ?? '').trim();
    if (!div) {
      return of([]);
    }

    if (this.scopeCache.has(div)) {
      return of(this.scopeCache.get(div)!);
    }

    const existingLoader = this.scopeLoaders.get(div);
    if (existingLoader) {
      return existingLoader;
    }

    const base = (environment.authApiUrl ?? '').replace(/\/+$/, '');
    const url = `${base}/division/${encodeURIComponent(div)}/rule-scopes`;

    const loader = this.http.get<RuleScope[]>(url).pipe(
      map((scopes) => (Array.isArray(scopes) ? scopes : [])),
      tap((scopes) => {
        this.scopeCache.set(div, scopes);
        this.scopeLoaders.delete(div);
      }),
      catchError((err) => {
        console.error('Failed to load rule scopes:', err);
        this.scopeLoaders.delete(div);
        return of([] as RuleScope[]);
      }),
      shareReplay(1)
    );

    this.scopeLoaders.set(div, loader);
    return loader;
  }

  /**
   * Evaluate a rule from cached scopes (call loadRuleScopes first).
   */
  isRuleApplicable(ruleName: string, divisionCode: string, warehouseCode: string): boolean {
    const div = String(divisionCode ?? '').trim();
    const rule = String(ruleName ?? '').trim();
    const warehouse = String(warehouseCode ?? '').trim();

    if (!div || !rule) {
      return false;
    }

    const scopes = this.scopeCache.get(div) ?? [];
    const scope = scopes.find(
      (s) => (s.ruleName || '').trim().toUpperCase() === rule.toUpperCase()
    );

    if (!scope) {
      return false;
    }

    if (scope.applyToAllWarehouses) {
      return true;
    }

    if (!warehouse) {
      return false;
    }

    return (scope.warehouses ?? []).some(
      (w) => String(w).trim() === warehouse
    );
  }

  clearScopeCache(divisionCode?: string): void {
    if (divisionCode) {
      const div = String(divisionCode).trim();
      this.scopeCache.delete(div);
      this.scopeLoaders.delete(div);
      return;
    }
    this.scopeCache.clear();
    this.scopeLoaders.clear();
  }

  /**
   * Fetch all rules for a specific division (legacy endpoint).
   */
  getRulesByDivision(divisionCode: string): Observable<Rule[]> {
    const div = String(divisionCode ?? '').trim();
    const base = (environment.authApiUrl ?? '').replace(/\/+$/, '');
    const url = `${base}/division/${encodeURIComponent(div)}`;

    return this.http.get<Rule[]>(url).pipe(
      catchError((err) => {
        console.error('Failed to load rules by division:', err);
        return of([] as Rule[]);
      })
    );
  }

  /**
   * Legacy division-only rule check (used by OUT_OF_STOCK_QTY until Phase 2).
   */
  checkRuleForDivision(divisionCode: string, ruleName: string): Observable<boolean> {
    const div = String(divisionCode ?? '').trim();
    const rule = String(ruleName ?? '').trim();
    const publishToSubject = rule.toUpperCase() === OUT_OF_STOCK_RULE;

    return this.getRulesByDivision(div).pipe(
      map((response: Rule[]) => {
        if (!Array.isArray(response) || response.length === 0) {
          if (publishToSubject) {
            this.ruleActiveForDivisionSubject.next(false);
          }
          return false;
        }

        const isRuleActive = response.some((r: Rule) => {
          const nameMatch = (r.ruleName || '').trim().toUpperCase() === rule.toUpperCase();
          const activeMatch = !!r.isActive;
          const divMatch = Array.isArray(r.divisions)
            ? r.divisions.some((d) => String(d.divisionCode).trim() === div)
            : false;
          return nameMatch && activeMatch && divMatch;
        });

        if (publishToSubject) {
          this.ruleActiveForDivisionSubject.next(isRuleActive);
        }
        return isRuleActive;
      }),
      catchError(() => {
        if (publishToSubject) {
          this.ruleActiveForDivisionSubject.next(false);
        }
        return of(false);
      })
    );
  }
}
